import { PropsWithChildren } from 'react';
import noop from 'lodash/noop';

import { useTranslation } from '@lyearn/i18n';
import { Shuffle2Outline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, InfoPill, Stack, Tooltip, Typography } from '@lyearn/ui';
import RichTextViewer from '@/components/RichTextViewer';
import useSubworkspaces from '@/modules/corporate/modules/workspaceSettings/hooks/useSubworkspaces';
import { QuestionEditorAccessType } from '@/modules/question/components/QuestionEditorModal/types';
import useSkills from '@/modules/skills/hooks/useSkills';
import { DifficultyEnum, QuestionTypeEnum, UpdateQuestionEntity } from '@/types/schema';

import { QUESTION_TYPE_CONFIG } from '../../../../../../../../components/picklist';
import { QUESTION_DIFFICULTY_CONFIG } from '../QuestionsSettings/components/DifficultyLevel/config';
import RemoveablePill from '../QuestionsSettings/components/SkillMeasured/components/RemoveablePill';
import { settingsContainerStyles } from '../QuestionsSettings/styles';

const WithLabel: React.FC<
  PropsWithChildren<{
    label: string;
  }>
> = ({ label, children }) => {
  return (
    <Stack direction="col" gapY={12}>
      <Typography className="ms-4" variant="label-02">
        {label}
      </Typography>
      {children}
    </Stack>
  );
};

const DifficultySection = ({ difficulty }: { difficulty: DifficultyEnum }) => {
  const { t } = useTranslation();
  const Icon = QUESTION_DIFFICULTY_CONFIG[difficulty].Icon;
  const labelKey = QUESTION_DIFFICULTY_CONFIG[difficulty].labelKey;

  return (
    <WithLabel label={t('QuizCreator:QuestionsSettings.Difficulty')}>
      <InfoPill
        Icon={Icon}
        appearance="primary"
        label={t(labelKey)}
        shape="square"
        size="large"
        type="icon"
        variant="outlined"
      />
    </WithLabel>
  );
};

const SubWorkspaceIdsSection = ({ subWorkspaceIds }: { subWorkspaceIds: string[] }) => {
  const { t } = useTranslation();

  const { subWorkspaces, fetching } = useSubworkspaces();

  const selectedWorkspaces = subWorkspaces?.filter((subWorkspace) =>
    subWorkspaceIds.includes(subWorkspace.id),
  );

  if (fetching) {
    return null;
  }

  return (
    <WithLabel label={t('Common:Form.Workspaces')}>
      {subWorkspaceIds.includes('all') ? (
        <Typography className="ms-8" color="text-text-placeholder" variant="body-long-01">
          {t('QuizCreator:QuestionsSettings.AllSubWorkspaces')}
        </Typography>
      ) : (
        <Stack className="mt-12 flex w-full flex-1 flex-wrap content-start" gapX={8} gapY={12}>
          {selectedWorkspaces?.map((workspace) => {
            const { id, name } = workspace;

            return (
              <RemoveablePill key={id} allowUpdates={false} id={id} name={name} onRemove={noop} />
            );
          })}
        </Stack>
      )}
    </WithLabel>
  );
};

const ViewQuestionSettings = ({
  question,
  accessType,
}: {
  question: UpdateQuestionEntity;
  accessType?: QuestionEditorAccessType;
}) => {
  const { t } = useTranslation();
  const difficulty = question.difficulty;
  const correctAnswerFeedback = question.correctAnswerFeedback;
  const incorrectAnswerFeedback = question.incorrectAnswerFeedback;
  const distractors = question.globalDistractors;
  const typeInfo = QUESTION_TYPE_CONFIG[question.questionType ?? QuestionTypeEnum.SingleSelect];
  const skillIds = question.skillIds?.set;
  const subWorkspaceIds = question.subWorkspaceIds?.set;

  const { skills } = useSkills({
    ids: skillIds ? skillIds : null,
    searchText: null,
    pause: !skillIds?.length,
  });

  return (
    <Stack
      className={cn('overflow-y-auto overflow-x-hidden px-20 pt-24 pb-32', settingsContainerStyles)}
      direction="col"
      gapY={40}>
      {question.questionType && typeInfo ? (
        <Stack alignItems="center" className="h-40 rounded-8 border border-border py-8 px-10">
          <Box className="flex h-24 w-24 items-center justify-center rounded-4 bg-bg-tertiary">
            <typeInfo.Icon className="h-18 w-18 flex-shrink-0" fillColorToken="icon-secondary" />
          </Box>
          <Typography className="ms-10" variant="body-short-02">
            {t(typeInfo.labelKey)}
          </Typography>
          {question.settings?.shuffleOptions ? (
            <Tooltip title={t('QuizCreator:QuestionsSettings.ShuffleOptionsViewDescription')}>
              <Shuffle2Outline className="h-20 w-20 text-link-internal ms-auto hover:text-link-internal-hover" />
            </Tooltip>
          ) : null}
        </Stack>
      ) : null}
      <Box className="w-fit space-y-20">
        {difficulty ? <DifficultySection difficulty={difficulty} /> : null}
        {skillIds?.length ? (
          <WithLabel label={t('Common:Routes.Skills')}>
            <Box className="flex flex-wrap gap-8">
              {skills?.map((skill) => {
                return (
                  <InfoPill
                    key={skill.id}
                    label={skill.name}
                    shape="square"
                    type="text"
                    variant="outlined"
                  />
                );
              })}
            </Box>
          </WithLabel>
        ) : null}
      </Box>
      {distractors?.set.length ? (
        <WithLabel label={t('QuizCreator:QuestionsSettings.Distractors')}>
          <Stack className="w-full" direction="col" gapY={8}>
            {distractors.set.map((distractor, index) => {
              return (
                <Box key={index} className="rounded-8 bg-bg-secondary p-12 pb-16">
                  <Typography color="text-text-paragraph" variant="body-long-02">
                    {distractor.text}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </WithLabel>
      ) : null}
      {correctAnswerFeedback || incorrectAnswerFeedback ? (
        <Box className="w-full space-y-20">
          {correctAnswerFeedback ? (
            <WithLabel label={t('QuizCreator:QuestionsSettings.FeedbackOnCorrectAnswer')}>
              <RichTextViewer
                color="text-text-paragraph"
                containerClassName="p-12 pb-16 bg-bg-secondary rounded-8"
                html={correctAnswerFeedback}
                variant="body-long-02"
              />
            </WithLabel>
          ) : null}
          {incorrectAnswerFeedback ? (
            <WithLabel label={t('QuizCreator:QuestionsSettings.FeedbackOnIncorrectAnswer')}>
              <RichTextViewer
                color="text-text-paragraph"
                containerClassName="p-12 pb-16 bg-bg-secondary rounded-8"
                html={incorrectAnswerFeedback}
                variant="body-long-02"
              />
            </WithLabel>
          ) : null}
        </Box>
      ) : null}
      {accessType === 'question-bank' && subWorkspaceIds?.length ? (
        <SubWorkspaceIdsSection subWorkspaceIds={subWorkspaceIds} />
      ) : null}
    </Stack>
  );
};

export default ViewQuestionSettings;
